import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { Field, reduxForm, formValueSelector } from "redux-form/immutable"
import withImmutablePropsToJS from "with-immutable-props-to-js"
import randomstring from "randomstring"
import debounce from "lodash.debounce"

import InfiniteScroll from "react-infinite-scroller"

import ExplainedToggle from "app/static/frontend/components/ExplainedToggle"
import { LoadingSpinner } from "app/static/frontend/components/LoadingSpinner"

// selectors
import {
    selectIsCreator,
    dashboardFormConnection,
    dashboardEditFormConnection,
} from "app/static/frontend/dashboards/selectors/dashboardTableSelectors"
// action-creators
import {
    chooseDashboardFormTemplate,
    clearDashboardTemplateList,
    createDashboard,
    editDashboard,
    getDashboardTemplates,
    validateDashboardCreation,
} from "dashboards/action-creators"
import { loadCustomRegions } from "app/static/frontend/subfeatures/RegionSelectionPage/action-creators"

// componentns
import { ModalBody, ModalContainer, ModalFooter, HorizontalFormStep, TemplateOptions } from "quorumdesign"
import InputField from "app/static/frontend/forms/components/InputField"
import RegionSelectField from "app/static/frontend/forms/components/RegionSelectField"

// needed for DashboardLimitedToTeamsFormComponent
import createConnectedTeamSelectSection from "shared/forms/LimitedToTeams/createConnectedTeamSelectSection"
import { makeJsIfExistsSelector, createEnableLimitedToTeamsOptions } from "shared/imports/sharedHelperFunctions"
import TeamSelectSection from "app/static/frontend/forms/components/TeamSelectSection"
import ShareExternallyLimitedToTeamsFormWrapper from "app/static/frontend/forms/components/ShareExternallyLimitedToTeamsFormWrapper"
import DesktopLimitedToTeamsSection from "app/static/frontend/forms/components/DesktopLimitedToTeamsSection"
import PublicFormComponent, { ExternalFormPasswordComponent } from "sheets/components/PublicFormComponent"

import sheetConstants from "sheets/constants"

// form
import {
    DASHBOARD_FORM_KEY,
    DASHBOARD_EDIT_FORM_KEY,
    DASHBOARD_EXTERNAL_REGION_TOOLTIP,
} from "shared/imports/sharedConstants"
import { ActionIcon, Button, Icon } from "@/components"
import {
    ModalButtonContainer,
    ModalDescription,
    ModalTitle,
    ModalHeader,
} from "app/static/frontend/dashboards/components/DashboardModalForms.styles"
import { CollapsibleSearchInput } from "@/components/CollapsibleSearchInput/CollapsibleSearchInput"

const getDashboardLimitedToTeamsFormComponent = (form, modalBodyRef) =>
    createConnectedTeamSelectSection({
        onMenuOpen: async () => {
            const { current } = modalBodyRef

            // onMenuOpen fires before react-select is finished rendering the full .Select__menu
            await new Promise((r) => setTimeout(r, 10))

            // scroll to the bottom of the 'Set Up Your New Dashboard'
            // since opening the TeamSelect DiscreteSelect in TeamSelectSection
            // can cause oveflow to occur in the ModalBody
            // https://stackoverflow.com/a/270628/6201291
            current.scrollTop = current.scrollHeight
        },
        enableLimitedToTeamsOptions: createEnableLimitedToTeamsOptions("dashboard"),
        formLabel: "Permissions",
        formName: form,
        isReduxForm: true,
        isCreatorSelector: form === DASHBOARD_FORM_KEY ? () => true : selectIsCreator,
        isLimitedToCreatorSelector: (state) => formValueSelector(form)(state, "is_limited_to_creator"),
        selector: makeJsIfExistsSelector((state) => formValueSelector(form)(state, "limited_to_teams")),
        WrapperComponent: ShareExternallyLimitedToTeamsFormWrapper,
        LimitedToTeamsSection: DesktopLimitedToTeamsSection,
        TeamSelectSection,
        name: "limited_to_teams",
        label: "Teams",
        shouldResetSection: false,
    })

// TODO: placeholders to be moved to selector or a constants file.
export const dashboardCreateFormText = "Customize your dashboard from scratch or use a customizable template"

const getDashboardLimitedToTeamField = (form, step) => (params, modalBodyRef, isEditing) => {
    const dashboardLimitedToTeamsFormComponent = getDashboardLimitedToTeamsFormComponent(form, modalBodyRef)

    return (
        <Field
            name="limited_to_teams"
            component={dashboardLimitedToTeamsFormComponent}
            props={{
                step,
                params,
                reduxFormKey: form,
                isEditing,
                useDesignSystemFormStep: true,
                formLabel: "Set Permissions",
            }}
        />
    )
}

const getExternalDashboardFields = (props, step) => {
    const externalSlug = props.external_slug || randomstring.generate({ length: 20, charset: "alphabetic" })
    const publicUrl = `${window.location.origin}/dashboard/external/${externalSlug}/`
    const iframeStyle = sheetConstants.IFRAME_STYLE
    const iFrameUrl = `<iframe src="${publicUrl}" ${iframeStyle}"></iframe>`

    // this is necessary to allow loading initial value from props but still mutate the form value with PublicFormComponent
    const is_external = props._is_external === undefined ? props.is_external : props._is_external
    const passwordRequired = props._passwordRequired === undefined ? props.passwordRequired : props._passwordRequired
    const externalPassword = props._externalPassword === undefined ? props.external_password : props._externalPassword
    const externalDownloadsDisabled =
        props._externalDownloadsDisabled === undefined
            ? props.externalDownloadsDisabled
            : props._externalDownloadsDisabled

    return (
        <HorizontalFormStep label="External Sharing" step={step} isModal={true}>
            <PublicFormComponent
                dataCy="public-dash"
                is_public={is_external}
                onChange={(val) => props.change("is_external", val)}
                onExplanation="This dashboard will be visible externally if visited at this url"
                offExplanation="This dashboard will not be visible externally"
                publicUrl={publicUrl}
                showEmbed={true}
                publicIframeUrl={iFrameUrl}
            />
            {is_external && (
                <React.Fragment>
                    <ExternalFormPasswordComponent
                        resourceName="Dashboard"
                        passwordRequired={passwordRequired}
                        externalPassword={externalPassword}
                        updatePasswordRequired={() => props.change("passwordRequired", !passwordRequired)}
                        changePassword={(val) => props.change("external_password", val.target.value)}
                    />
                    <ExplainedToggle
                        off="Downloads Disabled"
                        on="Downloads Enabled"
                        active={!externalDownloadsDisabled}
                        data-cy="external-dashboard-downloads-disabled"
                        offExplanation="This external dashboard will have downloads disabled"
                        onChange={(externalDownloadsDisabledValue) =>
                            props.change("externalDownloadsDisabled", !externalDownloadsDisabledValue)
                        }
                        onExplanation="This external dashboard will have downloads enabled"
                    />
                    <Field
                        name="external_regions"
                        component={RegionSelectField}
                        dataType={DjangIO.app.models.QuorumDataType.dashboard}
                        tooltipText={DASHBOARD_EXTERNAL_REGION_TOOLTIP}
                    />
                </React.Fragment>
            )}
        </HorizontalFormStep>
    )
}

const getCreateDashboardLimitedToTeamField = getDashboardLimitedToTeamField(DASHBOARD_FORM_KEY, 3)
const getEditDashboardLimitedToTeamField = getDashboardLimitedToTeamField(DASHBOARD_EDIT_FORM_KEY, 2)

export const UnconnectedDashboardCreateForm = (props) => {
    const modalRef = useRef()
    const modalBodyRef = useRef()
    const [submitting, updateSubmit] = useState(false)
    const [filterValue, updateFilter] = useState("")
    const [selected, updateSelected] = useState()

    useEffect(() => {
        props.getDashboardTemplates({
            offset: 0,
            // if user is a Quorum Demo account or superuser logged in as a user, show all templates
            filters: Userdata.is_demo_account || Userdata.logged_in_as_user ? {} : { not_demo_template: true },
        })
        // needed for setting dashboard external regions
        props.loadCustomRegions()
    }, [])

    const premadeTemplates = props.dashboardTemplates
        ? props.dashboardTemplates.map((template) => ({
              ...template,
              value: template.id,
              iconFamily: template.icon === "dashboard" ? "q" : "",
              selected: selected === template.id,
          }))
        : []

    const templateCount = premadeTemplates.length
    const initialLoad = !props.dashboardTemplates && props.isLoadingTemplates

    return (
        <React.Fragment>
            <ModalContainer
                onClose={() => props.togglePage(0)}
                optionalHandleClickOutside={(e) => {
                    // We have a swal (modal on modal) validation. We dont want the user to exit the form when
                    // interacting with the swal, so only exit if the click target is not a swal (clicking inside
                    // a swal is technically clicking "outside the (form) modal" and so we need to handle this)
                    const swalOverlay = document.getElementsByClassName("swal-overlay")[0]
                    if (!(swalOverlay && swalOverlay.contains(e.target))) {
                        props.togglePage(0)
                    }
                }}
            >
                <ModalHeader>
                    <ModalTitle>{props.page === 1 ? "New dashboard" : "Set Up Your New Dashboard"}</ModalTitle>
                    <ActionIcon onClick={() => props.togglePage(0)}>
                        <Icon icon="plus" />
                    </ActionIcon>
                </ModalHeader>
                {props.page === 1 ? (
                    <ModalBody
                        text={<ModalDescription>{dashboardCreateFormText}</ModalDescription>}
                        clampWidth={false}
                        rightSideComponent={
                            <CollapsibleSearchInput
                                placeholder="Search"
                                onChange={debounce((e) => {
                                    // empty the stored templates since they don't match the search terms
                                    props.clearDashboardTemplateList()

                                    // load new templates matching the search term
                                    props.getDashboardTemplates({
                                        offset: 0,
                                        filters:
                                            Userdata.is_demo_account || Userdata.logged_in_as_user
                                                ? { quick_search: e }
                                                : { not_demo_template: true, quick_search: e },
                                    })
                                    updateFilter(e)
                                    updateSelected()
                                }, 750)}
                            />
                        }
                    >
                        {initialLoad ? (
                            <LoadingSpinner id="dashboard-modal-loader" />
                        ) : (
                            <InfiniteScroll
                                className="list-view list-view-results"
                                data-cy="list-view-results"
                                hasMore={props.hasMoreTemplates}
                                initialLoad={false}
                                loader={<LoadingSpinner />}
                                loadMore={debounce(
                                    () =>
                                        props.getDashboardTemplates({
                                            offset: templateCount,
                                            filters:
                                                Userdata.is_demo_account || Userdata.logged_in_as_user
                                                    ? { quick_search: filterValue }
                                                    : { not_demo_template: true, quick_search: filterValue },
                                        }),
                                    1000,
                                )}
                                threshold={150}
                                useWindow={false}
                            >
                                <TemplateOptions
                                    selectTemplate={(template) => {
                                        props.change("widgets", template.widgets)
                                        props.change("positions", template.positions)
                                        return updateSelected(template.value)
                                    }}
                                    templateCount={templateCount}
                                    templates={premadeTemplates}
                                    smooth
                                />
                            </InfiniteScroll>
                        )}
                    </ModalBody>
                ) : (
                    <span ref={modalRef}>
                        <ModalBody
                            clampWidth={false}
                            modalBodyRef={modalBodyRef}
                            text={
                                <ModalDescription>
                                    Before configuring your new dashboard, give it a name and set editing and sharing
                                    permissions.
                                </ModalDescription>
                            }
                        >
                            <HorizontalFormStep label="Name Your Dashboard" step={1}>
                                <Field
                                    component={InputField}
                                    hideError
                                    label="Name"
                                    name="name"
                                    props={{
                                        style: { fontSize: "16px", height: "50px" },
                                        placeholder: "Name your dashboard...",
                                        className: "form-control",
                                        dataCy: "name-dashboard",
                                    }}
                                    showAsterisk
                                />
                            </HorizontalFormStep>
                            {getExternalDashboardFields(props, 2)}
                            {getCreateDashboardLimitedToTeamField(
                                props.params,
                                modalBodyRef,
                                props.isEditingTeamPermission,
                            )}
                        </ModalBody>
                    </span>
                )}
                <ModalFooter
                    rightSide={
                        <ModalButtonContainer>
                            <Button type="button" radius="xl" variant="outline" onClick={() => props.togglePage(0)}>
                                Cancel
                            </Button>
                            {props.page === 1 ? (
                                <Button
                                    type="button"
                                    variant="filled"
                                    radius="xl"
                                    dataCy="continue-dashboard"
                                    disabled={!props.selectedTemplate}
                                    onClick={() => props.togglePage(2)}
                                >
                                    Continue
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="filled"
                                    radius="xl"
                                    dataCy="submit-dashboard"
                                    onClick={async (...args) => {
                                        updateSubmit(true)
                                        await props.handleSubmit(...args)
                                        updateSubmit(false)
                                    }}
                                    disabled={submitting || !props.valid}
                                >
                                    {submitting ? "Submitting..." : "Create Dashboard"}
                                </Button>
                            )}
                        </ModalButtonContainer>
                    }
                />
            </ModalContainer>
        </React.Fragment>
    )
}

const DashboardReduxForm = reduxForm({
    form: DASHBOARD_FORM_KEY,
    onSubmit: createDashboard,
    touchOnBlur: true,
    touchOnChange: true,
    // needed for setting dashboard external regions
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    validate: validateDashboardCreation,
})(withImmutablePropsToJS(UnconnectedDashboardCreateForm))

export const DashboardCreateForm = connect(dashboardFormConnection, {
    chooseDashboardFormTemplate,
    clearDashboardTemplateList,
    getDashboardTemplates,
    loadCustomRegions,
})(DashboardReduxForm)

export const UnconnectedDashboardEditForm = (props) => {
    const modalBodyRef = useRef()
    const [submitting, updateSubmit] = useState(false)
    return (
        <ModalContainer onClose={() => props.closeModal()}>
            <ModalHeader
                hideCloseButton={true}
                onClose={() => props.closeModal()}
                title={"Edit Your Dashboard Permissions"}
            />
            <ModalBody modalBodyRef={modalBodyRef} text={"Change who can view and edit this Dashboard"}>
                {getExternalDashboardFields(props, 1)}
                {!Object.values(Userdata.persona_dashboards).includes(parseInt(props.dashboardId)) &&
                    getEditDashboardLimitedToTeamField(props.params, modalBodyRef, props.isEditingTeamPermission)}
            </ModalBody>
            <ModalFooter
                leftSide={
                    <Button isGiant type={"secondary"} onClick={() => props.closeModal()}>
                        Cancel
                    </Button>
                }
                rightSide={
                    <Button
                        isGiant
                        disabled={submitting}
                        onClick={async (...args) => {
                            updateSubmit(true)
                            await props.handleSubmit(...args)
                            updateSubmit(false)
                            props.closeModal()
                        }}
                    >
                        {submitting ? "Submitting..." : "Save Permissions"}
                    </Button>
                }
            />
        </ModalContainer>
    )
}

export const DashboardEditPermissionsReduxForm = reduxForm({
    form: DASHBOARD_EDIT_FORM_KEY,
    onSubmit: editDashboard,
    touchOnBlur: true,
    touchOnChange: true,
    // needed for setting dashboard external regions
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
})(UnconnectedDashboardEditForm)

export const DashboardEditForm = connect(dashboardEditFormConnection, {})(DashboardEditPermissionsReduxForm)
